import { useCallback } from 'react';

import { useQuery } from '@tanstack/react-query';

import { useGameSlug, useGetCompetitionCollections, useGetFantasyCollectionValidity } from '@pkg/hooks';

import { CollectionChainType, CompetitionCollectionStatus } from '@app/client';
import { callGetFreshIdeas } from '@app/client/resources/competitions';
import { type FreshCollection } from '@app/store/zustand';

const FRESH_IDEAS_COLLECTIONS_CACHE = 'FRESH_IDEAS_COLLECTIONS_CACHE';

type UseFantasyTopCollectionsProps = {
    disabled?: boolean;
};

export const useFreshIdeas = ({ disabled }: UseFantasyTopCollectionsProps) => {
    const slug = useGameSlug();
    const cache = [FRESH_IDEAS_COLLECTIONS_CACHE, slug];

    return useQuery(
        cache,
        () =>
            callGetFreshIdeas({
                competitionSlug: slug,
            }),
        {
            enabled: !disabled && !!slug,
            keepPreviousData: false,
            retry: 1,
            select: (res) => {
                if (res.ok && res.value) {
                    return res.value
                        .map((collectionData) => ({
                            ...collectionData.general,
                            chain: CollectionChainType.Ethereum,
                            salesVolume: collectionData.salesVolume || undefined,
                            metadata: collectionData.general.metadata.parsed,
                            valid: collectionData.valid,
                        }))
                        .filter((col) => col.valid) as FreshCollection[];
                }
            },
        },
    );
};

export function useCheckCollectionValidity() {
    const getCollectionStatus = useGetCompetitionCollections();
    const checkCollectionValidity = useGetFantasyCollectionValidity();

    return useCallback(
        async (collections?: FreshCollection[], doCriteriaCheck = false) => {
            const slugs = collections?.map((col) => col.slug);
            if (slugs?.length) {
                let slugsToCheck: string[];
                if (doCriteriaCheck) {
                    //because the mock data items don't get checked before this point
                    const validityData = await checkCollectionValidity({ collectionSlugs: slugs });
                    slugsToCheck = slugs.filter((slug) => validityData?.[slug]?.is_valid);
                } else {
                    slugsToCheck = slugs;
                }

                if (slugsToCheck?.length) {
                    const filteredCollections = collections?.filter((col) => slugsToCheck.includes(col.slug));
                    const statuses = await getCollectionStatus(slugsToCheck);
                    if (statuses?.length) {
                        return filteredCollections?.filter((item) => {
                            const notNormal = statuses?.find((statusItem) => {
                                // todo - see if we can up-dup from useCheckCollectionValidityFreshCollections
                                return (
                                    statusItem.slug === item.slug &&
                                    statusItem.status &&
                                    statusItem.status !== CompetitionCollectionStatus.Normal
                                );
                            });

                            return !notNormal;
                        });
                    }
                }
            }
        },
        [checkCollectionValidity, getCollectionStatus],
    );
}
